/**
 * Prints a self-contained HTML document via a hidden iframe and the browser's
 * native print dialog. Used for 80mm thermal receipts that go out through the
 * till's local (USB) printer driver — the server never touches the printer.
 *
 * Returns false when the iframe document is inaccessible so callers can fall
 * back (e.g. open a PDF) and surface an error.
 */
export function printReceiptHtml(html: string): boolean {
    const iframe = document.createElement('iframe')
    iframe.setAttribute('aria-hidden', 'true')
    iframe.setAttribute('data-receipt-print', '')
    iframe.style.position = 'fixed'
    iframe.style.right = '0'
    iframe.style.bottom = '0'
    iframe.style.width = '0'
    iframe.style.height = '0'
    iframe.style.border = '0'
    document.body.appendChild(iframe)

    const doc = iframe.contentDocument ?? iframe.contentWindow?.document
    const win = iframe.contentWindow
    if (!doc || !win) {
        iframe.remove()
        return false
    }

    const cleanup = () => {
        if (document.body.contains(iframe)) iframe.remove()
    }

    const triggerPrint = () => {
        try {
            win.focus()
            win.print()
        } finally {
            // afterprint is unreliable across browsers; reap the iframe on a timer.
            win.addEventListener('afterprint', cleanup)
            setTimeout(cleanup, 60000)
        }
    }

    doc.open()
    doc.write(html)
    doc.close()

    // Give embedded images (QR) and layout a tick to settle before printing.
    if (doc.readyState === 'complete') {
        setTimeout(triggerPrint, 100)
    } else {
        iframe.addEventListener('load', () => setTimeout(triggerPrint, 100), {
            once: true,
        })
    }
    return true
}
